import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { APP_TYPE } from '../constants/appType';
import { useSecretsStore, type SecretsMap } from '../stores/secretsStore';
import { useTenantId } from '../stores/tenantStore';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

type SecretRow = { key: string; value: string };
type SecretsResponse = { data: SecretRow[] };

const buildEnvsUrl = (): string => {
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    throw new Error(
      'VITE_API_URL is not set. The app cannot fetch tenant secrets.',
    );
  }
  return `${apiUrl}/v1/envs/${APP_TYPE}`;
};

const fetchSecrets = async (tenantId: string): Promise<SecretsMap> => {
  const res = await fetch(buildEnvsUrl(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'tenant-id': tenantId,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch secrets: ${res.status} ${res.statusText}`);
  }

  const body = (await res.json()) as SecretsResponse;
  const rows = Array.isArray(body?.data) ? body.data : [];

  return rows.reduce<SecretsMap>((acc, row) => {
    if (row?.key) acc[row.key] = row.value ?? '';
    return acc;
  }, {});
};

export const useSecretsQuery = () => {
  const setSecrets = useSecretsStore((s) => s.setSecrets);
  const tenantId = useTenantId();

  const query = useQuery({
    queryKey: ['secrets', APP_TYPE, tenantId],
    queryFn: () => fetchSecrets(tenantId as string),
    enabled: !!tenantId,
    refetchInterval: FIVE_MINUTES_MS,
    staleTime: FIVE_MINUTES_MS,
    refetchOnWindowFocus: true,
    retry: 3,
  });

  useEffect(() => {
    if (query.data) {
      setSecrets(query.data);
    }
  }, [query.data, setSecrets]);

  return query;
};
